/**
 * Shared futures ranking helpers — single source of truth for both
 * FOExplorePage and FOSection (Market Overview).
 *
 * Ranking metric: contracts_traded (volume / lot_size) primary,
 *                 turnover (volume × price) secondary.
 * Matches broker behavior: normalizes across lot sizes so penny stocks
 * don't dominate the "Top Traded" lists.
 */

/** Format expiry from ISO date (YYYY-MM-DD) to "DD Mon" display string */
export const formatExpiryFromISO = (isoDate) => {
  if (!isoDate) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  const day = parseInt(parts[2], 10);
  const month = parseInt(parts[1], 10) - 1;
  return `${day} ${months[month]}`;
};

/** Sort comparator: contracts_traded (primary) then turnover (secondary) — "Top Traded" */
export const sortByContracts = (a, b) =>
  (b.contracts_traded || 0) - (a.contracts_traded || 0) ||
  (b.turnover || 0) - (a.turnover || 0);

/** Sort comparator: turnover (primary) then OI (secondary) — "Most Active"
 *  Naturally down-weights penny/low-notional instruments. */
export const sortByTurnover = (a, b) =>
  (b.turnover || 0) - (a.turnover || 0) ||
  (b.oi || 0) - (a.oi || 0) ||
  (b.contracts_traded || 0) - (a.contracts_traded || 0);

/**
 * WebSocket tick-driven re-ranking.
 * Re-sorts the list using live tick volume / lot_size for fair ranking,
 * falling back to the REST-hydrated contracts_traded when no tick is available.
 *
 * @param {Array} items       - The base list (from REST hydration)
 * @param {Object} tickMap    - Lookup: key → tick object (from useRealtimeData)
 * @param {string} keyField   - Which item field to use as tickMap key (default 'ticker')
 */
export function reRankByTicks(items, tickMap, keyField = 'ticker') {
  if (!items.length) return items;
  return [...items].sort((a, b) => {
    const aTick = tickMap[a[keyField]];
    const bTick = tickMap[b[keyField]];
    const aVol = aTick?.volume_traded ?? aTick?.volume ?? 0;
    const bVol = bTick?.volume_traded ?? bTick?.volume ?? 0;
    const aLot = a.lot_size || 1;
    const bLot = b.lot_size || 1;
    const aContracts = aVol > 0 ? Math.floor(aVol / aLot) : (a.contracts_traded || 0);
    const bContracts = bVol > 0 ? Math.floor(bVol / bLot) : (b.contracts_traded || 0);
    if (bContracts !== aContracts) return bContracts - aContracts;
    // Tie-break: turnover
    const aPrice = aTick?.last_price ?? a.ltp ?? 0;
    const bPrice = bTick?.last_price ?? b.ltp ?? 0;
    return (bVol * bPrice) - (aVol * aPrice);
  });
}

/**
 * WebSocket tick-driven re-ranking for "Most Active" mode.
 * Uses turnover (volume × price) as the primary sort, which naturally
 * down-weights penny/low-notional instruments.
 */
export function reRankByTicksTurnover(items, tickMap, keyField = 'ticker') {
  if (!items.length) return items;
  return [...items].sort((a, b) => {
    const aTick = tickMap[a[keyField]];
    const bTick = tickMap[b[keyField]];
    const aVol = aTick?.volume_traded ?? aTick?.volume ?? 0;
    const bVol = bTick?.volume_traded ?? bTick?.volume ?? 0;
    const aPrice = aTick?.last_price ?? a.ltp ?? 0;
    const bPrice = bTick?.last_price ?? b.ltp ?? 0;
    const aTurnover = aVol > 0 ? aVol * aPrice : (a.turnover || 0);
    const bTurnover = bVol > 0 ? bVol * bPrice : (b.turnover || 0);
    if (bTurnover !== aTurnover) return bTurnover - aTurnover;
    const aOi = aTick?.oi ?? 0;
    const bOi = bTick?.oi ?? 0;
    if (bOi !== aOi) return bOi - aOi;
    const aLot = a.lot_size || 1;
    const bLot = b.lot_size || 1;
    const aContracts = aVol > 0 ? Math.floor(aVol / aLot) : (a.contracts_traded || 0);
    const bContracts = bVol > 0 ? Math.floor(bVol / bLot) : (b.contracts_traded || 0);
    return bContracts - aContracts;
  });
}
